/* Blanket Statement — shared behaviour. Load deferred in theme.liquid:
   <script src="{{ 'bs-theme.js' | asset_url }}" defer></script> */
(function () {
  'use strict';

  // Mark that JS is alive BEFORE anything hides itself. Reveal styles are
  // scoped to .bs-js, so if this line never runs the page renders fully
  // visible instead of blank. (v1 shipped opacity:0 unconditionally.)
  var root = document.documentElement;
  if (!('IntersectionObserver' in window)) return;
  root.classList.add('bs-js');

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  ready(function () {
    /* A sticky theme header covers whatever is pinned to the top of the viewport
       — most visibly the customizer's blanket on a phone, which then reads as cut
       in half. Publish the header's height as --bs-sticky-top so anything sticky
       can offset itself, and re-measure on resize because it differs between
       breakpoints. Nothing sticky should assume the top of the window is free. */
    function bsSetStickyTop() {
      var h = 0;
      var cands = document.querySelectorAll('#header-group, sticky-header, header, .header-wrapper');
      for (var i = 0; i < cands.length; i++) {
        var cs = window.getComputedStyle(cands[i]);
        if (cs.position === 'sticky' || cs.position === 'fixed') {
          h = Math.max(h, Math.round(cands[i].getBoundingClientRect().height));
        }
      }
      document.documentElement.style.setProperty('--bs-sticky-top', h + 'px');
    }
    bsSetStickyTop();
    window.addEventListener('resize', bsSetStickyTop);
    window.addEventListener('load', bsSetStickyTop);

    /* ---- Scroll reveal ----------------------------------------------
       Reveal-on-scroll has one classic failure: anything the viewport skips
       over never fires an intersection, so it stays invisible forever. That
       happens on anchor links (#faq), on a restored scroll position after
       reload, on fast flick-scrolling, and on in-page jumps. The guard is to
       treat "above the viewport" as already-seen rather than waiting.       */

    function reveal(el) {
      el.classList.add('is-in');
      el.style.transitionDelay = '';
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        var passed = e.boundingClientRect.bottom < 0;   // scrolled clean past it
        if (e.isIntersecting || passed) {
          if (passed) e.target.style.transitionDelay = '0ms';
          reveal(e.target);
          io.unobserve(e.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.06 });

    function observe(el, i) {
      if (el.dataset.bsRevealBound) return;
      el.dataset.bsRevealBound = '1';
      // Anything already at or above the fold on first paint is shown at once —
      // animating content the visitor is already looking at just delays it.
      if (el.getBoundingClientRect().bottom < 0) { reveal(el); return; }
      el.style.transitionDelay = Math.min(i % 4, 3) * 70 + 'ms';
      io.observe(el);
    }

    function scan(root) {
      (root || document).querySelectorAll('.bs-reveal').forEach(observe);
    }
    scan();

    // Sections added later — theme editor, or anything injected after load.
    document.addEventListener('shopify:section:load', function (e) { scan(e.target); });
    window.bsRevealScan = scan;

    // Belt and braces: on any scroll, release anything still hidden that is no
    // longer below the fold. Cheap, runs once per frame at most, and it means a
    // missed intersection can never leave a blank screen.
    var ticking = false;
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        ticking = false;
        document.querySelectorAll('.bs-reveal:not(.is-in)').forEach(function (el) {
          var r = el.getBoundingClientRect();
          if (r.top < window.innerHeight * 0.94) { reveal(el); io.unobserve(el); }
        });
      });
    }, { passive: true });

    /* ---- Sticky CTA bar ---------------------------------------------
       Appears once the hero has scrolled past, hides again at the footer
       CTA so two identical buttons are never on screen at once.        */
    var bar = document.querySelector('[data-bs-sticky-cta]');
    if (bar) {
      var trigger = document.querySelector('[data-bs-sticky-trigger]') ||
                    document.querySelector('.bs-section-hero');
      var stop    = document.querySelector('[data-bs-sticky-stop]');
      var pastHero = false, atStop = false;

      function sync() { bar.classList.toggle('is-shown', pastHero && !atStop); }

      if (trigger) {
        new IntersectionObserver(function (es) {
          pastHero = !es[0].isIntersecting; sync();
        }, { threshold: 0 }).observe(trigger);
      } else { pastHero = true; sync(); }

      if (stop) {
        new IntersectionObserver(function (es) {
          atStop = es[0].isIntersecting; sync();
        }, { threshold: 0 }).observe(stop);
      }
    }

    /* ---- FAQ accordion ----------------------------------------------
       <details> does the work; this only animates and enforces one-open. */
    document.querySelectorAll('[data-bs-faq]').forEach(function (group) {
      var items = group.querySelectorAll('details');
      items.forEach(function (d) {
        d.addEventListener('toggle', function () {
          if (!d.open) return;
          items.forEach(function (o) { if (o !== d) o.open = false; });
        });
      });
    });

    /* ---- Before/after comparison slider ------------------------------
       Driven by pointer events on the image, not by the range input.

       The range was invisible and stretched over the whole stage, which is a
       poor touch target at the best of times — but it could not work at all
       here, because the stage carries `touch-action: none` to stop the page
       scrolling under a drag, and that also stops the browser handling the
       range's own touch gestures. On a phone the handle simply did not move.

       Dragging anywhere on the picture is what people reach for anyway. The
       range stays for keyboard and screen-reader users; `touch-action: pan-y`
       lets the page still scroll vertically through the section.           */
    document.querySelectorAll('[data-bs-compare]').forEach(function (el) {
      var range = el.querySelector('input[type="range"]');
      var top   = el.querySelector('[data-bs-compare-top]');
      if (!top) return;

      var pos = range ? parseFloat(range.value) : 50;

      function paint() {
        top.style.clipPath = 'inset(0 ' + (100 - pos) + '% 0 0)';
        el.style.setProperty('--bs-compare-pos', pos + '%');
        if (range) range.value = pos;
      }

      function setFromX(clientX) {
        var r = el.getBoundingClientRect();
        if (!r.width) return;
        pos = Math.max(0, Math.min(100, ((clientX - r.left) / r.width) * 100));
        paint();
      }

      /* Chrome starts an image drag on mousedown, before our pointermove ever
         runs, so the gesture became "peel the photo off the page". Refusing
         dragstart on the whole stage is the part that actually stops it; the CSS
         handles the cases where a drag would start without a dragstart event. */
      el.addEventListener('dragstart', function (e) { e.preventDefault(); });

      var dragging = false, pid = null;
      el.addEventListener('pointerdown', function (e) {
        e.preventDefault();
        dragging = true; pid = e.pointerId;
        if (el.setPointerCapture) el.setPointerCapture(pid);
        setFromX(e.clientX);
      });
      el.addEventListener('pointermove', function (e) {
        if (!dragging || e.pointerId !== pid) return;
        e.preventDefault();
        setFromX(e.clientX);
      });
      ['pointerup', 'pointercancel'].forEach(function (t) {
        el.addEventListener(t, function (e) {
          if (e.pointerId !== pid) return;
          dragging = false; pid = null;
        });
      });

      if (range) range.addEventListener('input', function () {
        pos = parseFloat(range.value); paint();
      });

      paint();
    });
  });
})();

/* ---------------------------------------------------------------------------
   Dropdown menus in the header

   Two separate faults had to be fixed here.

   1. The theme hides a submenu with visibility:hidden and reveals it only when
      its parent link carries aria-expanded="true" — and nothing in the theme
      ever sets that for a plain nested menu. So the dropdown could not open at
      all, and the click just followed the parent's href and reloaded the page.

   2. Once it did open, it still did not appear. The panel sits inside
      header-component.header, which carries `contain: layout style`. That makes
      the header a stacking context, and the panel cannot paint outside it no
      matter what z-index it is given — verified by setting z-index 99999 and a
      magenta background and watching the hero still hit-test on top at every
      point inside the panel's own box.

      A trapped stacking context cannot be escaped with z-index; the element has
      to leave. So on open the panel is moved to <body>, positioned under the
      header, and moved back to its original place on close. A comment marks its
      seat so it always returns to the right spot.

   The parent link keeps its href: a Shopify menu item must have one, and it is a
   real destination without JavaScript. With JS the click toggles instead — a
   parent that opens a list of children should not also navigate away from the
   page those children point into.
   ------------------------------------------------------------------------ */
(function () {
  var group = document.querySelector('#header-group');
  if (!group) return;

  var parents = [].slice.call(group.querySelectorAll('a[aria-haspopup="true"]'));
  if (!parents.length) return;

  var open = null;   // { link, panel, seat }

  function place(panel, link) {
    /* Measure the header itself, not #header-group: that wrapper is static and
       its sticky child is taken out of its flow, so the group reports zero
       height and the panel landed at the top of the window, over the header. */
    var bar = group.querySelector('header-component.header, .header, .shopify-section');
    var header = (bar || group).getBoundingClientRect();
    var anchor = link.getBoundingClientRect();

    panel.style.position = 'fixed';
    panel.style.top = Math.round(header.bottom) + 'px';
    panel.style.right = 'auto';
    panel.style.width = 'max-content';
    panel.style.zIndex = '130';   /* over the page, under the size guide at 140 */
    panel.style.visibility = 'visible';

    /* Anchored under its own link rather than stretched across the window: a
       full-width band of eight stacked links reads as a broken page, not a menu.
       Nudged back inside the viewport if the link sits near the right edge. */
    panel.style.left = '0';
    var w = panel.getBoundingClientRect().width;
    var left = Math.min(Math.max(8, anchor.left), Math.max(8, window.innerWidth - w - 8));
    panel.style.left = Math.round(left) + 'px';
  }

  function close() {
    if (!open) return;
    open.link.setAttribute('aria-expanded', 'false');
    /* Back to its own seat, with every inline style removed so the theme's own
       rules govern it again. */
    if (open.seat && open.seat.parentNode) {
      open.seat.parentNode.insertBefore(open.panel, open.seat);
      open.seat.parentNode.removeChild(open.seat);
    }
    open.panel.classList.remove('bs-submenu--portal');
    open.panel.removeAttribute('style');
    open = null;
  }

  function show(link) {
    var item = link.closest('li');
    var panel = item && item.querySelector('.menu-list__submenu');
    if (!panel) return;
    close();
    var seat = document.createComment('bs-submenu');
    panel.parentNode.insertBefore(seat, panel);
    document.body.appendChild(panel);
    panel.classList.add('bs-submenu--portal');
    place(panel, link);
    link.setAttribute('aria-expanded', 'true');
    open = { link: link, panel: panel, seat: seat };
  }

  parents.forEach(function (link) {
    var item = link.closest('li');
    if (!item || !item.querySelector('.menu-list__submenu')) return;
    link.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      if (open && open.link === link) close(); else show(link);
    });
  });

  /* Anywhere else closes it — including a click on one of the children, which
     are same-page anchors, so the panel would otherwise sit over the section the
     customer just asked to see. */
  document.addEventListener('click', function (e) {
    if (!open) return;
    if (e.target.closest && e.target.closest('.menu-list__submenu')) { close(); return; }
    close();
  });

  document.addEventListener('keydown', function (e) { if (e.key === 'Escape') close(); });
  window.addEventListener('resize', function () { if (open) place(open.panel, open.link); });
  window.addEventListener('scroll', function () { if (open) place(open.panel, open.link); }, { passive: true });
})();
